import { ChevronLeft, ChevronRight } from "lucide-react";
import "./style.css";

export type PaginationProps = {
  /** Current page number (1-indexed) */
  current: number;
  /** Items per page */
  pageSize: number;
  /** Total number of items */
  total: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Callback when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
  /** Optional callback for API page calculation (e.g., for chunked backends) */
  onApiPageChange?: (apiPage: number) => void;
  /** Row size options */
  pageSizeOptions?: number[];
  /** Chunk size for calculating API pages (used with onApiPageChange) */
  chunkSize?: number;
  /** Label for the showing text */
  itemLabel?: string;
  /** Custom class name */
  className?: string;
  /** Show/hide the size selector */
  showSizeChanger?: boolean;
  /** Hide pagination on single page */
  hideOnSinglePage?: boolean;
};

// A sliding window of page numbers centred on the current page (1-indexed).
const getPageWindow = (current: number, pageCount: number, span = 5): number[] => {
  if (pageCount <= span) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  let start = Math.max(1, current - Math.floor(span / 2));
  const end = Math.min(pageCount, start + span - 1);
  start = Math.max(1, end - span + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

const Pagination: React.FC<PaginationProps> = ({
  current,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  onApiPageChange,
  pageSizeOptions = [25, 50, 100],
  chunkSize,
  itemLabel = "items",
  className = "",
  showSizeChanger = true,
  hideOnSinglePage = false,
}) => {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > pageCount || newPage === current) return;
    onPageChange(newPage);

    if (onApiPageChange && chunkSize) {
      const apiPage = Math.ceil((newPage * pageSize) / chunkSize);
      onApiPageChange(apiPage);
    }
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPageSize = Number(e.target.value);
    onPageSizeChange?.(newPageSize);
    onPageChange(1); // Reset to first page

    if (onApiPageChange && chunkSize) {
      onApiPageChange(1);
    }
  };

  const startRecord = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const endRecord = Math.min(current * pageSize, total);
  const pages = getPageWindow(current, pageCount);
  const showPager = !(hideOnSinglePage && pageCount <= 1);

  return (
    <div className={`ui-table-pagination-footer ${className}`.trim()}>
      <div className="ui-pagination-showing-text">
        {total > 0
          ? `Showing ${startRecord} to ${endRecord} of ${total} ${itemLabel}`
          : `No ${itemLabel}`}
      </div>

      <div className="ui-pagination-controls">
        {showSizeChanger && (
          <div className="ui-pagination-size">
            <span className="ui-pagination-rows-label">Rows</span>
            <select
              className="ui-pagination-rows-select"
              value={String(pageSize)}
              onChange={handlePageSizeChange}
              aria-label="Items per page"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}

        {showPager && (
          <nav className="ui-pagination-pager" aria-label="Pagination">
            <button
              type="button"
              className="ui-page-arrow"
              disabled={total === 0 || current <= 1}
              onClick={() => handlePageChange(current - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} strokeWidth={2} />
            </button>
            {pages.map((p) => (
              <button
                type="button"
                key={p}
                className={p === current ? "ui-page-num active" : "ui-page-num"}
                aria-current={p === current ? "page" : undefined}
                disabled={total === 0}
                onClick={() => handlePageChange(p)}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              className="ui-page-arrow"
              disabled={total === 0 || current >= pageCount}
              onClick={() => handlePageChange(current + 1)}
              aria-label="Next page"
            >
              <ChevronRight size={16} strokeWidth={2} />
            </button>
          </nav>
        )}
      </div>
    </div>
  );
};

export { Pagination };
